import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import type { HiddenCatalogRule } from '@/types';

/**
 * Admin management of which folders and reports non-admins may see.
 *
 * This screen is a management view, not the control. Enforcement is server-side on every endpoint
 * that names a report — catalogue search, report parameters, subscription create, subscription
 * detail and list, and run history — so a rule added here holds against anyone calling the API
 * directly, not just against this SPA.
 *
 * The rules only ever subtract. Nothing here can grant sight of a report the caller's own
 * report-server permissions already deny.
 */
export function HiddenReportsCard() {
  const api = useApi();
  const queryClient = useQueryClient();

  const [path, setPath] = useState('');
  const [scope, setScope] = useState<'Folder' | 'Item'>('Folder');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const rules = useQuery({ queryKey: ['hidden-reports'], queryFn: api.listHiddenReports });
  const diagnostics = useQuery({ queryKey: ['visibility'], queryFn: api.visibilityDiagnostics });

  // Suggestions come from the catalogue search, which is itself permission-trimmed. Folders never
  // appear there — the search only offers reports — so the path stays a free-text field and the
  // suggestions are a convenience for the single-report case.
  const suggestions = useQuery({
    queryKey: ['catalog', 'hide-suggest', path],
    queryFn: () => api.searchCatalog(path || undefined),
    enabled: path.trim().length >= 2 && scope === 'Item'
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['hidden-reports'] });
    void queryClient.invalidateQueries({ queryKey: ['catalog'] });
  };

  const hide = useMutation({
    mutationFn: () => api.hideReport({ path: path.trim(), scope, reason: reason.trim() || null }),
    onSuccess: () => {
      setPath('');
      setReason('');
      setError(null);
      invalidate();
    },
    onError: (e: Error) => setError(e.message)
  });

  const unhide = useMutation({
    mutationFn: (id: number) => api.unhideReport(id),
    onSuccess: invalidate,
    onError: (e: Error) => setError(e.message)
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!path.trim()) {
      setError('Enter a catalogue path, for example /Automated');
      return;
    }
    hide.mutate();
  };

  return (
    <div className="card">
      <div className="card-header">
        <h3>Report visibility</h3>
      </div>
      <div className="card-body">
        <div className="notice">
          Hidden folders and reports are removed from the catalogue, the report picker, run history
          and every subscription view for everyone except admins — enforced by the API, not just
          here. Hiding never grants access: a report someone&rsquo;s own report-server permissions
          already deny stays denied.
        </div>

        {diagnostics.data && (
          <div className={diagnostics.data.permissionsResolved ? 'notice' : 'notice notice-warn'}>
            Visibility mode <strong>{diagnostics.data.mode}</strong>
            {diagnostics.data.detail ? ` — ${diagnostics.data.detail}` : ''}
            {!diagnostics.data.permissionsResolved &&
              '. Nothing can be shown until this is resolved; the catalogue will appear empty.'}
          </div>
        )}

        {error && <div className="error-banner">{error}</div>}

        <form onSubmit={submit}>
          <div className="field-row">
            <div className="field">
              <label htmlFor="hide-path">Catalogue path</label>
              <input
                id="hide-path"
                className="input"
                value={path}
                placeholder="/Automated"
                onChange={e => setPath(e.target.value)}
              />
              <div className="field-hint">
                The full path as PBIRS holds it. A trailing slash is fine — it is normalised.
              </div>
            </div>

            <div className="field">
              <label htmlFor="hide-scope">Scope</label>
              <select
                id="hide-scope"
                className="input"
                value={scope}
                onChange={e => setScope(e.target.value as 'Folder' | 'Item')}
              >
                <option value="Folder">Folder and everything under it</option>
                <option value="Item">This report only</option>
              </select>
            </div>
          </div>

          {scope === 'Item' && (suggestions.data?.length ?? 0) > 0 && (
            <div className="param-options">
              {(suggestions.data ?? []).slice(0, 8).map(item => (
                <button
                  type="button"
                  key={item.path}
                  className="param-option"
                  onClick={() => setPath(item.path)}
                >
                  <span className="param-option-value">{item.path}</span>
                </button>
              ))}
            </div>
          )}

          <div className="field">
            <label htmlFor="hide-reason">Reason (optional)</label>
            <input
              id="hide-reason"
              className="input"
              value={reason}
              placeholder="Why this is not offered for self-service"
              onChange={e => setReason(e.target.value)}
            />
          </div>

          <button type="submit" className="btn-primary" disabled={hide.isPending}>
            {hide.isPending ? 'Hiding…' : 'Hide from non-admins'}
          </button>
        </form>

        <table className="data-table">
          <thead>
            <tr>
              <th>Path</th>
              <th>Scope</th>
              <th>Reason</th>
              <th>Added by</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(rules.data ?? []).map((rule: HiddenCatalogRule) => (
              <tr key={rule.id}>
                <td><div className="report-path">{rule.path}</div></td>
                <td>{rule.scope === 'Folder' ? 'Folder + contents' : 'Single report'}</td>
                <td>{rule.reason ?? '—'}</td>
                <td>{rule.createdBy}</td>
                <td>
                  <button
                    type="button"
                    className="btn-danger btn-sm"
                    disabled={unhide.isPending}
                    onClick={() => unhide.mutate(rule.id)}
                  >
                    Unhide
                  </button>
                </td>
              </tr>
            ))}
            {(rules.data?.length ?? 0) === 0 && (
              <tr>
                <td colSpan={5} className="placeholder">Nothing is hidden.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
